import type { SharePointRepository } from "../../domain/repositories/sharepoint-repository"
import type {
  DriveRef,
  GraphItem,
  SiteItem,
  SyncFailure,
  SyncStatus,
} from "../../domain/entities/sharepoint"
import { fetchWithAuth } from "@/core/data/fetch-with-auth"
import { parseResponse } from "@/core/data/parse-response"

type GraphItemModel = {
  id: string
  name: string
  is_folder: boolean
  size: number
  drive_id: string
}

export const toGraphItem = (m: GraphItemModel): GraphItem => ({
  id: m.id,
  name: m.name,
  isFolder: m.is_folder,
  size: m.size,
  driveId: m.drive_id,
})

type SyncFailureModel = {
  name: string
  error: string
  category: string
  retryable: boolean
  drive_id: string
  item_id: string
  folder?: string | null
  is_folder: boolean
}

export const toSyncFailure = (m: SyncFailureModel): SyncFailure => ({
  name: m.name,
  error: m.error,
  category: m.category,
  retryable: m.retryable,
  driveId: m.drive_id,
  itemId: m.item_id,
  folder: m.folder ?? undefined,
  isFolder: m.is_folder,
})

export const httpSharePointRepository: SharePointRepository = {
  startAuth: async (kbId) => {
    const res = await fetchWithAuth(
      `/api/v1/sharepoint/auth/start?kb_id=${encodeURIComponent(kbId)}`,
    )
    const data = await parseResponse<{ authorize_url: string }>(res)
    return data.authorize_url
  },

  exchangeCode: async (code, state) => {
    const res = await fetchWithAuth(`/api/v1/sharepoint/auth/exchange`, {
      method: "POST",
      body: JSON.stringify({ code, state }),
    })
    const data = await parseResponse<{ connection_id: string }>(res)
    return data.connection_id
  },

  listSites: async (connectionId) => {
    const res = await fetchWithAuth(
      `/api/v1/sharepoint/sites?connection_id=${encodeURIComponent(connectionId)}`,
    )
    const data = await parseResponse<{
      sites: { id: string; name: string; web_url: string }[]
    }>(res)
    return data.sites.map(
      (s): SiteItem => ({ id: s.id, name: s.name, webUrl: s.web_url }),
    )
  },

  listSiteDrives: async (connectionId, siteId) => {
    const res = await fetchWithAuth(
      `/api/v1/sharepoint/sites/${encodeURIComponent(siteId)}/drives?connection_id=${encodeURIComponent(connectionId)}`,
    )
    const data = await parseResponse<{ drives: { drive_id: string; name: string }[] }>(
      res,
    )
    return data.drives.map((d): DriveRef => ({ driveId: d.drive_id, name: d.name }))
  },

  listShared: async (connectionId) => {
    const res = await fetchWithAuth(
      `/api/v1/sharepoint/shared?connection_id=${encodeURIComponent(connectionId)}`,
    )
    const data = await parseResponse<{ items: GraphItemModel[] }>(res)
    return data.items.map(toGraphItem)
  },

  listOneDrive: async (connectionId) => {
    const res = await fetchWithAuth(
      `/api/v1/sharepoint/onedrive?connection_id=${encodeURIComponent(connectionId)}`,
    )
    const data = await parseResponse<{ items: GraphItemModel[] }>(res)
    return data.items.map(toGraphItem)
  },

  listChildren: async (connectionId, driveId, itemId, all) => {
    const res = await fetchWithAuth(
      `/api/v1/sharepoint/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/children?connection_id=${encodeURIComponent(connectionId)}${all ? "&all=true" : ""}`,
    )
    const data = await parseResponse<{ items: GraphItemModel[] }>(res)
    return data.items.map(toGraphItem)
  },

  searchItems: async (connectionId, driveId, itemId, q) => {
    const res = await fetchWithAuth(
      `/api/v1/sharepoint/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/search?connection_id=${encodeURIComponent(connectionId)}&q=${encodeURIComponent(q)}`,
    )
    const data = await parseResponse<{ items: GraphItemModel[] }>(res)
    return data.items.map(toGraphItem)
  },

  sync: async ({ connectionId, kbId, items }) => {
    const res = await fetchWithAuth(`/api/v1/sharepoint/sync`, {
      method: "POST",
      body: JSON.stringify({
        connection_id: connectionId,
        kb_id: kbId,
        items: items.map((i) => ({
          drive_id: i.driveId,
          item_id: i.itemId,
          name: i.name,
          size: i.size,
          is_folder: i.isFolder,
        })),
      }),
    })
    const data = await parseResponse<{ sync_id: string }>(res)
    return data.sync_id
  },

  pollSync: async (syncId) => {
    const res = await fetchWithAuth(
      `/api/v1/sharepoint/sync/${encodeURIComponent(syncId)}`,
    )
    const data = await parseResponse<{
      status: string
      total: number
      done: number
      failed: SyncFailureModel[]
    }>(res)
    return {
      status: data.status,
      total: data.total,
      done: data.done,
      failed: (data.failed ?? []).map(toSyncFailure),
    } satisfies SyncStatus
  },
}
